import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import cities from "../utils/cities/cities.json";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CMSContext } from "../components/CaseManagement/context/CaseManagmentContext";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import InputMask from "inputmask";
import PhoneInput from "react-phone-input-2";
import { showInsufficientCreditsModal } from "../actions/creditActions";
import { deductCredits } from "../utils/HelperFunction/deductCredits";
import { createClientApi, updateClientApi } from "../services/api/caseManagementApi";

const AddNewClientDialog = ({ open, onClose, data = {}, preview = false }) => {
  const [filteredCities, setFilteredCities] = useState(cities);
  const [filteredProvinces, setFilteredProvinces] = useState([
    ...new Set(cities.map((city) => city.province)),
  ]);
  const [loading, setLoading] = useState(false);
  const { fetchClients } = useContext(CMSContext);
  const [countryCode, setCountryCode] = useState("pk");
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const cnicRef = useRef(null);
  const ownerId = userInfo._id;
  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      name: data?.name || "",
      phone: data?.phone || "",
      email: data?.email || "",
      cnic: data?.cnic || "",
      profession: data?.profession || "",
      address: data?.address || "",
      city: data?.city || "",
      province: data?.province || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      phone: Yup.string().required("Phone number is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      cnic: Yup.string()
        .matches(
          /^\d{5}-\d{7}-\d{1}$/,
          "Please enter a valid CNIC (e.g., 12345-1234567-1)"
        )
        .required("CNIC is required"),
      profession: Yup.string().required("Profession is required"),
      address: Yup.string().required("Address is required"),
      city: Yup.string().required("City is required"),
      province: Yup.string().required("Province is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);
        if (data?._id) {
          await saveClientApi(values, "PUT");
        } else {
          await saveClientApi(values, "POST");
        }
        resetForm();
        onClose(values);
      } catch (error) {
        setLoading(false);
        console.error("Error saving case:", error);
      }
    },
  });

  const saveClientApi = async (caseData, method) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }

    const response =
      method === "PUT"
        ? await updateClientApi(data._id, { ...caseData, ownerId })
        : await createClientApi({ ...caseData, ownerId });

    if (!(response.status === 200 || response.status === 201)) {
      const errorText =
        method === "PUT" ? "Failed to update client" : "Failed to save client";
      toast.error(errorText);
      setLoading(false);
      throw new Error(errorText);
    }

    const responseData = response.data;
    const successText =
      method === "PUT"
        ? "Client Updated Successfully"
        : responseData?.message || "New Client Added Successfully";
    toast.success(successText);
    fetchClients();
    setLoading(false);

    const deductionSuccess = deductCredits(
      {
        userId,
        creditsToDeduct: 1,
        event: `${
          method === "PUT"
            ? "Edit Client on Case Management"
            : "Add new Client on Case Management"
        }`,
      },
      dispatch
    );

    if (!deductionSuccess) {
      console.warn("Failed to deduct credits. Ensure retry or user notification.");
    }
    return responseData;
  };

  useEffect(() => {
    if (cnicRef.current) {
      InputMask({ mask: "99999-9999999-9" }).mask(cnicRef.current);
    }
  }, [cnicRef.current]);

  useEffect(() => {
    if (open) {
      setFilteredCities(cities);
      setFilteredProvinces([...new Set(cities.map((city) => city.province))]);
    }
  }, [open, cities]);

  return (
    <Dialog
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          boxShadow: 24,
          p: 1,
          width: 500,
        },
      }}
      open={open}
      onClose={() => {
        formik.resetForm();
        onClose(null);
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          fontSize: "18px",
          fontWeight: "bold",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        {preview
          ? "Client Details"
          : Object.entries(data)?.length > 0
            ? "Edit Client"
            : "Add New Client"}
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <TextField
              autoComplete="nope"
              label="Name"
              fullWidth
              name="name"
              required
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              size="small"
              inputProps={{ readOnly: preview }}
              InputProps={{
                sx: {
                  bgcolor: "white",
                  fontSize: "14px",
                  "& input:-webkit-autofill": {
                    backgroundColor: "white !important",
                    WebkitBoxShadow: "0 0 0 30px white inset !important",
                    WebkitTextFillColor: "black !important",
                  },
                },
              }}
              InputLabelProps={{ sx: { fontSize: "14px" } }}
            />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <PhoneInput
                disabled={preview}
                country={countryCode}
                value={formik.values.phone}
                onChange={(phone, countryData) => {
                  formik.setFieldValue("phone", phone);
                  setCountryCode(countryData.countryCode);
                }}
                onBlur={formik.handleBlur("phone")}
                inputProps={{ name: "phone", required: true, autoFocus: false }}
                inputStyle={{
                  width: "100%",
                  height: "36px",
                  fontSize: "14px",
                  backgroundColor: "white",
                  borderColor:
                    formik.touched.phone && formik.errors.phone
                      ? "red"
                      : "#ced4da",
                  borderRadius: "4px",
                  paddingLeft: "48px",
                }}
                buttonStyle={{
                  borderColor:
                    formik.touched.phone && formik.errors.phone
                      ? "red"
                      : "#ced4da",
                  backgroundColor: "white",
                }}
                containerStyle={{ width: "100%" }}
              />
              {formik.touched.phone && formik.errors.phone && (
                <Typography color="error" sx={{ fontSize: "12px", mt: "2px", ml: "14px" }}>
                  {formik.errors.phone}
                </Typography>
              )}
            </Box>
            <TextField
              required
              autoComplete="nope"
              label="Email"
              fullWidth
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              size="small"
              inputProps={{ readOnly: preview }}
              InputProps={{
                sx: {
                  bgcolor: "white",
                  fontSize: "14px",
                  "& input:-webkit-autofill": {
                    backgroundColor: "white !important",
                    WebkitBoxShadow: "0 0 0 30px white inset !important",
                    WebkitTextFillColor: "black !important",
                  },
                },
              }}
              InputLabelProps={{ sx: { fontSize: "14px" } }}
            />
            <TextField
              required
              inputRef={cnicRef}
              autoComplete="nope"
              fullWidth
              label="CNIC"
              name="cnic"
              value={formik.values.cnic}
              onChange={formik.handleChange}
              error={formik.touched.cnic && Boolean(formik.errors.cnic)}
              helperText={formik.touched.cnic && formik.errors.cnic}
              size="small"
              inputProps={{ readOnly: preview }}
              InputProps={{
                sx: {
                  bgcolor: "white",
                  fontSize: "14px",
                  "& input:-webkit-autofill": {
                    backgroundColor: "white !important",
                    WebkitBoxShadow: "0 0 0 30px white inset !important",
                    WebkitTextFillColor: "black !important",
                  },
                },
              }}
              InputLabelProps={{ sx: { fontSize: "14px" } }}
            />
            <TextField
              required
              autoComplete="nope"
              label="Profession"
              fullWidth
              name="profession"
              value={formik.values.profession}
              onChange={formik.handleChange}
              error={formik.touched.profession && Boolean(formik.errors.profession)}
              helperText={formik.touched.profession && formik.errors.profession}
              inputProps={{ readOnly: preview }}
              size="small"
              InputProps={{
                sx: {
                  bgcolor: "white",
                  fontSize: "14px",
                  "& input:-webkit-autofill": {
                    backgroundColor: "white !important",
                    WebkitBoxShadow: "0 0 0 30px white inset !important",
                    WebkitTextFillColor: "black !important",
                  },
                },
              }}
              InputLabelProps={{ sx: { fontSize: "14px" } }}
            />
            <Box sx={{ display: "flex", gap: "5px" }}>
              <Autocomplete
                readOnly={preview}
                options={filteredProvinces}
                value={formik.values.province || null}
                size="small"
                getOptionLabel={(option) => option || ""}
                sx={{ width: "50%" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Select Province"
                    size="small"
                    name="province-select"
                    id="province-select"
                    inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        bgcolor: "white",
                        fontSize: { xs: "12px", sm: "14px" },
                        "& input:-webkit-autofill": {
                          backgroundColor: "white !important",
                          WebkitBoxShadow: "0 0 0 30px white inset !important",
                          WebkitTextFillColor: "black !important",
                        },
                      },
                    }}
                    InputLabelProps={{ sx: { fontSize: "14px" } }}
                    error={formik.touched.province && Boolean(formik.errors.province)}
                    helperText={formik.touched.province && formik.errors.province}
                  />
                )}
                componentsProps={{
                  paper: {
                    sx: {
                      "& .MuiAutocomplete-listbox": {
                        fontSize: "13px",
                        maxHeight: "120px",
                        overflowY: "auto",
                        "::-webkit-scrollbar": { width: "4px" },
                        "::-webkit-scrollbar-track": { background: "#fff" },
                        "::-webkit-scrollbar-thumb": { background: "#bbb", borderRadius: "4px" },
                      },
                    },
                  },
                }}
                onChange={(event, newValue) => {
                  formik.setFieldValue("province", newValue || null);
                  if (newValue) {
                    const citiesForProvince = cities.filter(
                      (city) => city.province === newValue
                    );
                    setFilteredCities(citiesForProvince);
                  } else {
                    formik.setFieldValue("city", "");
                    setFilteredCities(cities);
                  }
                }}
              />

              <Autocomplete
                readOnly={preview}
                options={filteredCities}
                value={filteredCities.find((c) => c.city === formik.values.city) || null}
                size="small"
                sx={{ width: "50%" }}
                getOptionLabel={(option) => option.city || ""}
                isOptionEqualToValue={(option, value) => option.city === value?.city}
                disabled={!formik.values.province}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    value={formik.values.city}
                    label="Select City"
                    size="small"
                    name="city-select"
                    id="city-select"
                    inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        bgcolor: "white",
                        fontSize: { xs: "12px", sm: "14px" },
                        "& input:-webkit-autofill": {
                          backgroundColor: "white !important",
                          WebkitBoxShadow: "0 0 0 30px white inset !important",
                          WebkitTextFillColor: "black !important",
                          boxShadow: "0 0 0 1000px white inset !important",
                        },
                      },
                    }}
                    InputLabelProps={{ sx: { fontSize: "14px" } }}
                    error={formik.touched.city && Boolean(formik.errors.city)}
                    helperText={formik.touched.city && formik.errors.city}
                  />
                )}
                componentsProps={{
                  paper: {
                    sx: {
                      "& .MuiAutocomplete-listbox": {
                        fontSize: "13px",
                        maxHeight: "120px",
                        overflowY: "auto",
                        "::-webkit-scrollbar": { width: "4px" },
                        "::-webkit-scrollbar-track": { background: "#fff" },
                        "::-webkit-scrollbar-thumb": { background: "#bbb", borderRadius: "4px" },
                      },
                    },
                  },
                }}
                onChange={(event, newValue) => {
                  formik.setFieldValue("city", newValue?.city || null);
                }}
              />
            </Box>
            <TextField
              required
              autoComplete="nope"
              label="Address"
              fullWidth
              name="address"
              value={formik.values.address}
              onChange={formik.handleChange}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
              size="small"
              inputProps={{ readOnly: preview }}
              InputProps={{
                sx: {
                  bgcolor: "white",
                  fontSize: "14px",
                  "& input:-webkit-autofill": {
                    backgroundColor: "white !important",
                    WebkitBoxShadow: "0 0 0 30px white inset !important",
                    WebkitTextFillColor: "black !important",
                  },
                },
              }}
              InputLabelProps={{ sx: { fontSize: "14px" } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => onClose(null)}
            sx={{
              borderRadius: "24px",
              padding: "8px 24px",
              textTransform: "none",
              fontWeight: "bold",
            }}
            variant="outlined"
            color="error"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            onClick={() => {
              formik.handleSubmit();
            }}
            sx={{
              borderRadius: "24px",
              padding: "8px 24px",
              textTransform: "none",
              fontWeight: "bold",
              backgroundColor: "#003D78",
              "&:hover": { backgroundColor: "primary.main", color: "#fff" },
              transition: "background-color 0.3s ease-in-out",
            }}
            disabled={preview || loading}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <CircularProgress size={18} sx={{ color: "#a9a9a9" }} />
                <Box>Adding...</Box>
              </Box>
            ) : data?._id ? (
              "Update Client"
            ) : (
              "Add Client"
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddNewClientDialog;
